import { useNavigate } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { CartTopNav } from "@/modules/cart/CartTopNav/CartTopNav";
import { Button } from "atoms/button/Button";
import { ImagePlaceholder } from "atoms/image-placeholder/ImagePlaceholder";
import { TextButton } from "atoms/text-button/TextButton";
import { formatCurrency } from "toolbox/Money";

import { goToSignIn } from "./anonymousBrowsingSession";
import { useIsAnonymousCheckout } from "./anonymousBrowsingState";
import {
  type AdvertisedOffer,
  type PendingCartItem,
  removePendingCartItem,
  removePendingOffer,
  useHasPurchasableItems,
  usePendingCartItems,
  usePendingOffers,
} from "./pendingCart";

type Props = {
  platformId: string;
  showNavbar: boolean;
};

/** The prices the visitor saw when they added the item, replayed back to them. */
const PendingCartPrice = ({ quote }: { quote: NonNullable<PendingCartItem["quote"]> }) => {
  const { t } = useTranslation();
  const isDiscounted = quote.displayedPriceInCents < quote.unitPriceInCents;

  return (
    <div className="flex flex-col gap-0.5" data-testid="anonymous-cart-item-price">
      <span className="text-body2 font-semibold text-primary">
        {formatCurrency(quote.displayedPriceInCents)}
        {isDiscounted && (
          <span className="ml-2 font-normal text-secondary line-through">
            {formatCurrency(quote.unitPriceInCents)}
          </span>
        )}
      </span>
      {quote.memberPriceInCents != null && (
        <span className="text-body2 text-secondary">
          {t("anonymousBrowsing.memberPrice", {
            price: formatCurrency(quote.memberPriceInCents),
          })}
        </span>
      )}
    </div>
  );
};

const PendingCartRow = ({
  item,
  showPrices,
  onRemove,
}: {
  item: PendingCartItem;
  showPrices: boolean;
  onRemove: () => void;
}) => {
  const { t } = useTranslation();
  const quantity = item.lineItem.quantity;

  return (
    <li className="flex items-center gap-4 bg-one p-4" data-testid="anonymous-cart-item">
      <ImagePlaceholder url={item.imageUrl} title={item.title} size="sm" className="rounded" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="line-clamp-2 font-sans text-bold1 text-primary">{item.title}</span>
        {quantity != null && (
          <span className="text-body2 text-secondary">
            {t("anonymousBrowsing.quantity", { count: quantity })}
          </span>
        )}
        {showPrices && item.quote && <PendingCartPrice quote={item.quote} />}
      </div>
      <TextButton xsText onClick={onRemove}>
        {t("anonymousBrowsing.remove")}
      </TextButton>
    </li>
  );
};

const PendingOfferRow = ({ offer, onRemove }: { offer: AdvertisedOffer; onRemove: () => void }) => {
  const { t } = useTranslation();

  return (
    <li className="flex items-center gap-4 bg-one p-4" data-testid="anonymous-cart-offer">
      <ImagePlaceholder title={offer.title} size="sm" className="rounded" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="line-clamp-2 font-sans text-bold1 text-primary">{offer.title}</span>
        <span className="text-body2 text-secondary">
          {t("anonymousBrowsing.offerAppliesAtCheckout")}
        </span>
      </div>
      <TextButton xsText onClick={onRemove}>
        {t("anonymousBrowsing.remove")}
      </TextButton>
    </li>
  );
};

const EstimatedSubtotal = ({ items }: { items: PendingCartItem[] }) => {
  const { t } = useTranslation();
  const subtotal = items.reduce(
    (total, item) =>
      total + (item.quote ? item.quote.displayedPriceInCents * (item.lineItem.quantity ?? 1) : 0),
    0,
  );

  return (
    <div
      className="m-4 flex flex-col gap-2 rounded bg-one p-4"
      data-testid="anonymous-cart-subtotal"
    >
      <div className="flex items-center justify-between">
        <span className="font-sans text-bold1 text-primary">
          {t("anonymousBrowsing.estimatedSubtotal")}
        </span>
        <span className="font-sans text-bold1 text-primary">{formatCurrency(subtotal)}</span>
      </div>
      <p className="text-body2 text-secondary">{t("anonymousBrowsing.estimatedTotalNote")}</p>
    </div>
  );
};

/**
 * `/cart` while browsing anonymously. With anonymous checkout off the local
 * pending cart is listed without any pricing (it is account-scoped, so it only
 * appears once the items have been replayed into the real cart) behind a
 * sign-in call to action. With checkout on the visitor sees the platform
 * prices they were quoted and goes straight to checkout.
 */
export const AnonymousCartView = ({ platformId, showNavbar }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const items = usePendingCartItems(platformId);
  const offers = usePendingOffers(platformId);
  const canCheckOut = useHasPurchasableItems(platformId);
  const anonymousCheckout = useIsAnonymousCheckout();

  if (items.length === 0 && offers.length === 0) {
    return (
      <>
        <CartTopNav showNavbar={showNavbar} />
        <div className="px-4">
          <div className="py-16 text-center font-serif text-h3">
            {t("anonymousBrowsing.cartEmpty")}
          </div>
          <Button fullWidth onClick={() => navigate("/shop")}>
            {t("anonymousBrowsing.keepBrowsing")}
          </Button>
        </div>
      </>
    );
  }

  return (
    <div className="flex min-h-full flex-col">
      <CartTopNav showNavbar={showNavbar} />
      <ul className="flex flex-1 flex-col gap-px bg-background-two">
        {items.map((item) => (
          <PendingCartRow
            key={item.id}
            item={item}
            showPrices={anonymousCheckout}
            onRemove={() => removePendingCartItem(platformId, item.id)}
          />
        ))}
        {offers.map((offer) => (
          <PendingOfferRow
            key={offer.id}
            offer={offer}
            onRemove={() => removePendingOffer(platformId, offer.id)}
          />
        ))}
      </ul>
      {anonymousCheckout && canCheckOut && <EstimatedSubtotal items={items} />}
      <div className="sticky bottom-0 flex flex-col gap-2 bg-white p-4 pb-[calc(16px_+_env(safe-area-inset-bottom))] shadow-footerNavigation">
        {anonymousCheckout && canCheckOut && (
          <>
            <Button fullWidth onClick={() => navigate("/cart/checkout")}>
              {t("anonymousBrowsing.checkout")}
            </Button>
            <p className="text-center text-body3 text-secondary">
              {t("anonymousCheckout.phoneTitle")}
            </p>
          </>
        )}
        {anonymousCheckout && !canCheckOut && (
          <Button fullWidth onClick={() => navigate("/shop")}>
            {t("anonymousBrowsing.keepBrowsing")}
          </Button>
        )}
        {!anonymousCheckout && (
          <>
            <Button fullWidth onClick={() => goToSignIn(navigate, "/cart")}>
              {t("anonymousBrowsing.logInForExclusivePricing")}
            </Button>
            <p className="text-center text-body2 text-secondary">
              {canCheckOut
                ? t("anonymousBrowsing.itemsWaitingAfterLogIn")
                : t("anonymousBrowsing.offersWaitingAfterLogIn")}
            </p>
          </>
        )}
      </div>
    </div>
  );
};
